/**
 * Compound add-message flow. <AddMessage> owns the stage; Trigger renders the
 * dashed pill with the role picker popover, Editor the role-aligned compose
 * bubble. Exactly one of them is visible at a time, driven by shared context.
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PillButton } from '../ui/pill-button';
import { RoleTile } from '../ui/role-tile';
import { TurnBubble, TurnLabel } from '../ui/turn';
import { ROLE_META, type SimulatorRole } from './roles';

interface AddMessageContextValue {
  stage: 'idle' | SimulatorRole;
  disabled: boolean;
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;
  choose: (role: SimulatorRole) => void;
  submit: (text: string) => void;
  cancel: () => void;
}

const AddMessageContext = createContext<AddMessageContextValue | null>(null);

const useAddMessage = () => {
  const ctx = useContext(AddMessageContext);
  if (!ctx) throw new Error('AddMessage.* must be rendered inside <AddMessage>');
  return ctx;
};

interface AddMessageProps {
  disabled?: boolean;
  onAdd?: (role: SimulatorRole, text: string) => void;
  children: ReactNode;
}

const AddMessageRoot = ({ disabled = false, onAdd, children }: AddMessageProps) => {
  const [stage, setStage] = useState<'idle' | SimulatorRole>('idle');
  const [pickerOpen, setPickerOpen] = useState(false);

  const value: AddMessageContextValue = {
    stage,
    disabled,
    pickerOpen,
    setPickerOpen,
    choose: (role) => {
      setPickerOpen(false);
      setStage(role);
    },
    submit: (text) => {
      if (stage !== 'idle') onAdd?.(stage, text);
      setStage('idle');
    },
    cancel: () => setStage('idle'),
  };

  return <AddMessageContext.Provider value={value}>{children}</AddMessageContext.Provider>;
};

const Trigger = ({ children = 'Add Message' }: { children?: ReactNode }) => {
  const { stage, disabled, pickerOpen, setPickerOpen, choose } = useAddMessage();
  if (stage !== 'idle') return null;
  return (
    <div className='flex justify-center'>
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <PillButton dashed disabled={disabled}>
            <Plus className='h-3.5 w-3.5' />
            {children}
          </PillButton>
        </PopoverTrigger>
        <PopoverContent
          side='bottom'
          sideOffset={6}
          className='flex w-auto gap-2 rounded-xl border-gray-500 bg-white p-2 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.2)]'
        >
          {(Object.keys(ROLE_META) as SimulatorRole[]).map((role) => (
            <RoleTile key={role} role={role} onSelect={choose} />
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};

const Editor = () => {
  const { stage, disabled, submit, cancel } = useAddMessage();
  const [text, setText] = useState('');
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const composing = stage !== 'idle';
  const trimmed = text.trim();

  useEffect(() => {
    if (composing) areaRef.current?.focus();
    else setText('');
  }, [composing]);

  if (!composing) return null;

  const meta = ROLE_META[stage];
  const isRight = meta.align === 'right';
  const handleAdd = () => {
    if (trimmed && !disabled) submit(trimmed);
  };

  return (
    <div className={cn('flex w-full', isRight ? 'justify-end' : 'justify-start')}>
      <div className={cn('flex w-[80%] flex-col gap-1', isRight ? 'items-end' : 'items-start')}>
        <TurnLabel icon={meta.icon}>
          <span className='font-medium'>{meta.label}</span>
        </TurnLabel>
        <TurnBubble tone={isRight ? 'agent' : 'customer'} className='w-full'>
          <Textarea
            ref={areaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAdd();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            placeholder={stage === 'user' ? 'Type what the customer says…' : 'Type what the agent says…'}
            className='min-h-[60px] resize-y border-0 bg-transparent p-0 text-sm shadow-none placeholder:text-text-disabled focus-visible:ring-0'
            disabled={disabled}
          />
          <div className='mt-1.5 flex items-center justify-end gap-2'>
            <PillButton variant='ghost' onClick={cancel}>
              Cancel
            </PillButton>
            <PillButton onClick={handleAdd} disabled={disabled || !trimmed}>
              <Plus className='h-3.5 w-3.5' /> Add
            </PillButton>
          </div>
        </TurnBubble>
      </div>
    </div>
  );
};

export const AddMessage = Object.assign(AddMessageRoot, { Trigger, Editor });
